import convert from 'convert-units';
import {
  IAggregate,
  IAggregateWithMetric,
  IMetric,
  ITest,
  ITestRepetition,
} from '../data/types';
/**
 * @description converts meters to inches
 *
 * @param {number} value the value to convert
 * @returns {number} the value in inches
 */
export const metersToInches = (value: number): number => {
  return convert(value).from('m').to('in');
};

/**
 * @description converts applicable (measured in meters) test values to imperial
 *
 * @param {ITest} test the test to convert
 * @returns {ITest} the converted test
 */
export const convertTestToImperial = <T extends ITest | ITestRepetition>(
  test: T
): T => {
  return Object.keys(test).reduce((acc: T, curr: string): T => {
    const currentMetric = test[curr];
    if (currentMetric === undefined || currentMetric === null) return acc;
    const convertedMetric = convertMetricToImperial(currentMetric);
    acc[curr] = convertedMetric;
    return acc;
  }, test);
};

/**
 * @description converts applicable (measured in meters) metric values to imperial
 *
 * @param {IMetric} metric the metric to convert
 * @returns {IMetric} the converted metric
 */
export const convertMetricToImperial = (metric: IMetric): IMetric => {
  if (metric.units === 'm') {
    metric.units = 'in';
    metric.value = metersToInches(metric.value);
  }
  return metric;
};

/**
 * @description converts an aggregates values from meters to inches
 *
 * @param {IAggregate} aggregate the aggregate to convert
 * @returns {IAggregate} the converted aggregate
 */
export const convertAggregateToImperial = (
  aggregate: IAggregate
): IAggregateWithMetric => {
  // tslint:disable:object-literal-sort-keys
  return {
    ...aggregate,
    topValues: aggregate.topValues.map(metersToInches),
    bottomValues: aggregate.bottomValues.map(metersToInches),
    mean: metersToInches(aggregate.mean),
    sum: metersToInches(aggregate.sum),
    populationSd: metersToInches(aggregate.populationSd),
    populationVariance: metersToInches(aggregate.populationVariance),
    sampleSd: metersToInches(aggregate.sampleSd),
    sampleVariance: metersToInches(aggregate.sampleVariance),
    units: 'in',
  };
  // tslint:enable
};

/**
 * @description converts centimeters to inches
 * @param val
 * @returns
 */
export const centimetersToInches = (val: number): number =>
  convert(val).from('cm').to('in');

/**
 * @description converts inches to centimeters
 * @param val
 * @returns
 */
export const inchesToCentimeters = (val: number): number =>
  convert(val).from('in').to('cm');

/**
 * @description converts newtons to lbs
 * @param val
 */
export const convertNewtonsToLbs = (val: number): number => {
  return val / 4.448;
};

/**
 * @description converts newtons to kgs
 * @param val
 * @returns
 */
export const convertNewtonsToKgs = (val: number): number => {
  return val / 9.81;
};

/**
 * @description converts kgs to newtons
 * @param val
 * @returns
 */
export const convertKgsToNewtons = (val: number): number => {
  return val * 9.81;
};

/**
 * @description parses a height string and converts to centimeters
 * Supports formats: "180", "180cm", "70in", "70\"", "5ft10in", "5'10\"", "5'10"
 * @param value the height string to parse
 * @returns height in centimeters, or undefined if invalid
 */
export const parseHeightToCentimeters = (
  value: string | number | undefined
): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  // If already a number, assume centimeters (reject non-positive)
  if (typeof value === 'number') {
    return value > 0 ? value : undefined;
  }

  const str = String(value).trim().toLowerCase();

  // Reject negative values
  if (str.startsWith('-')) {
    return undefined;
  }

  // Check for feet and inches format: "5ft10in", "5'10\"", "5'10", "5ft10"
  const feetInchesMatch = str.match(
    /^(\d+(?:\.\d+)?)\s*(?:ft|'|feet)\s*(\d+(?:\.\d+)?)?\s*(?:in|"|inches?)?$/
  );
  if (feetInchesMatch) {
    const feet = parseFloat(feetInchesMatch[1]);
    const inches = feetInchesMatch[2] ? parseFloat(feetInchesMatch[2]) : 0;
    const totalInches = feet * 12 + inches;
    return inchesToCentimeters(totalInches);
  }

  // Check for inches format: "70in", "70\"", "70 inches"
  const inchesMatch = str.match(/^(\d+(?:\.\d+)?)\s*(?:in|"|inches?)$/);
  if (inchesMatch) {
    const inches = parseFloat(inchesMatch[1]);
    return inchesToCentimeters(inches);
  }

  // Check for centimeters format: "180cm", "180 cm"
  const cmMatch = str.match(/^(\d+(?:\.\d+)?)\s*(?:cm|centimeters?)?$/);
  if (cmMatch) {
    return parseFloat(cmMatch[1]);
  }

  // If just a number string, assume centimeters (reject non-positive)
  const numValue = parseFloat(str);
  if (!isNaN(numValue) && numValue > 0) {
    return numValue;
  }

  return undefined;
};

/**
 * @description Auto-detects date format and converts to ISO (YYYY-MM-DD)
 * Supports: YYYY-MM-DD, MM/DD/YYYY, DD/MM/YYYY
 * Uses heuristics: if first number > 12, assumes European DD/MM format
 * NOTE: Ambiguous dates (e.g., 01/02/2000) will default to MM/DD/YYYY format
 * @param dateStr the date string to parse
 * @returns ISO date string (YYYY-MM-DD), or null if invalid
 */
export const parseDateToISO = (dateStr: string): string | null => {
  if (!dateStr || typeof dateStr !== 'string') {
    return null;
  }

  const str = dateStr.trim();
  if (str === '') {
    return null;
  }

  // Check for ISO format: YYYY-MM-DD
  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const year = parseInt(isoMatch[1], 10);
    const month = parseInt(isoMatch[2], 10);
    const day = parseInt(isoMatch[3], 10);
    if (isValidDate(year, month, day)) {
      return str;
    }
    return null;
  }

  // Check for slash-separated format: could be MM/DD/YYYY or DD/MM/YYYY
  const slashMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const first = parseInt(slashMatch[1], 10);
    const second = parseInt(slashMatch[2], 10);
    const year = parseInt(slashMatch[3], 10);

    let month: number, day: number;
    if (first > 12) {
      // European format: DD/MM/YYYY
      day = first;
      month = second;
    } else {
      // US format: MM/DD/YYYY (default)
      month = first;
      day = second;
    }

    if (isValidDate(year, month, day)) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
    return null;
  }

  // Check for 4-digit year only (for backwards compatibility)
  const yearOnlyMatch = str.match(/^(\d{4})$/);
  if (yearOnlyMatch) {
    const year = parseInt(yearOnlyMatch[1], 10);
    if (year >= 1900 && year <= new Date().getFullYear()) {
      return str; // Return as-is for year-only format
    }
    return null;
  }

  return null;
};

/**
 * @description Validates a date given year, month (1-12), and day
 * @param year
 * @param month 1-12
 * @param day
 * @returns true if valid date
 */
const isValidDate = (year: number, month: number, day: number): boolean => {
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  if (year < 1900 || year > new Date().getFullYear()) return false;

  // Check days in month
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  // Leap year check for February
  if (month === 2) {
    const isLeap =
      (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    if (isLeap && day > 29) return false;
    if (!isLeap && day > 28) return false;
  } else if (day > daysInMonth[month - 1]) {
    return false;
  }

  return true;
};

